'use client';

import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import { HOMEPAGE_MAX_VISIBLE_LIKES } from './constants.ts';
import styles from './styles.module.css';

import { HomepageProductCard } from '@/app/[locale]/(home)/components/HomepageProductCard';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
  ModuleInteractionType,
  moduleOriginParamName,
} from '@/modules/activityTracker/constants.ts';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker.ts';
import { type LikedProduct } from '@/modules/likes/types.ts';
import { getProductCardColumns } from '@/modules/recentlyViewed/helpers.ts';
import { appendParams } from '@/modules/routing/appendParams/appendParams.ts';
import { getProductSizeName } from '@/modules/sizes/helpers.ts';
import { useCurrentUser } from '@/modules/user/useCurrentUser.ts';

type Props = {
  products: LikedProduct[];
};

export function LikesUi({ products }: Props) {
  const tCommon = useTranslations('common');
  const [_, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  const isSeeMoreButtonVisible = products.length > HOMEPAGE_MAX_VISIBLE_LIKES;

  function handleProductClick(
    selectedProductId: number,
    selectedProductName: string,
    selectedProductPosition: number
  ) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: HomePageModuleNames.Likes,
      itemClicked: selectedProductName,
      itemId: String(selectedProductId),
      itemPosition: selectedProductPosition,
      schemaVersion: '3.0',
    });
  }

  function handleSeeMoreClick() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      {
        moduleName: HomePageModuleNames.Likes,
        interactionType: ModuleInteractionType.SeeMoreClicked,
        schemaVersion: '1.0',
      }
    );
  }

  return (
    <section>
      <ModuleHeader title={tCommon('MyLikes')} size={ModuleHeaderSize.Large}>
        {isSeeMoreButtonVisible && (
          <ExtendedLink
            href={appendParams(`${currentUser?.username}/likes/`, [
              moduleOriginParamName,
              HomePageModuleNames.Likes,
            ])}
            onClick={handleSeeMoreClick}
          >
            <Body variant="sm-regular" className={styles.seeMoreText}>
              {tCommon('SeeMore')}
            </Body>
          </ExtendedLink>
        )}
      </ModuleHeader>

      <ScrollableItemRow
        minItemWidth={144}
        itemsPerRow={getProductCardColumns(products.length)}
      >
        {products
          .slice(0, HOMEPAGE_MAX_VISIBLE_LIKES)
          .map((productItem, index) => (
            <li key={productItem.id} className={styles.productListItem}>
              <HomepageProductCard
                id={productItem.id}
                isLiked
                preview={productItem.preview}
                productHref={`${productItem.slug}?moduleOrigin=${HomePageModuleNames.Likes}`}
                pictures={productItem.pictures}
                displayPrice={productItem.pricing.display_price}
                hasVideo={productItem.has_video}
                size={
                  productItem.sizes &&
                  getProductSizeName(productItem.sizes, {
                    multipleSizes: tCommon('MultipleSizes'),
                  })
                }
                handleClick={() =>
                  handleProductClick(productItem.id, productItem.slug, index)
                }
                brandName={productItem.brand_name}
              />
            </li>
          ))}
      </ScrollableItemRow>
    </section>
  );
}
